import { BoxProps, ButtonProps, Grid } from '@chakra-ui/react';
import { DPCalendar, DPDay, useDatePicker } from '@rehookify/datepicker';
import { format as formatDate } from 'date-fns/format';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import MButton from '../MButton';
import MText from '../MText';
import { MBox } from '../chakra';

const REMOVE_LEADING_ZERO_REGEX = /^0+/;

interface CalendarViewProps {
  calendars: DPCalendar;
  weekDays: string[];
  onChange: (day: DPDay) => void;
  dayBtnPropsFn: ReturnType<typeof useDatePicker>['propGetters']['dayButton'];
  onEnter?: () => void;
  onNavigateMonth?: (direction: 'prev' | 'next') => void;
}

export const CalendarView = ({
  calendars,
  weekDays,
  onChange,
  dayBtnPropsFn,
  onEnter,
  onNavigateMonth,
}: CalendarViewProps) => {
  const { days, month, year } = calendars;
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(
    null,
  );

  // Determine which day gets tabIndex={0} (roving tabindex)
  const rovingIndex = useMemo(() => {
    const selectedIdx = days.findIndex(({ selected }) => selected);
    if (selectedIdx >= 0) {
      return selectedIdx;
    }
    const todayIdx = days.findIndex(
      ({ now, inCurrentMonth }) => now && inCurrentMonth,
    );
    if (todayIdx >= 0) {
      return todayIdx;
    }
    const currentMonthIdx = days.findIndex(
      ({ inCurrentMonth }) => inCurrentMonth,
    );
    return currentMonthIdx >= 0 ? currentMonthIdx : 0;
  }, [days]);

  // Focus the pending index after a month navigation re-render
  useEffect(() => {
    if (pendingFocusIndex !== null) {
      const el = buttonRefs.current[pendingFocusIndex];
      if (el) {
        el.focus();
      }
      setPendingFocusIndex(null);
    }
  }, [pendingFocusIndex, days]);

  const findNextEnabledIndex = useCallback(
    (startIndex: number, step: number): number | null => {
      let index = startIndex;
      while (index >= 0 && index < 42) {
        if (!days[index].disabled) {
          return index;
        }
        index += step;
      }
      return null;
    },
    [days],
  );

  const handleGridKeyDown = useCallback(
    (event: React.KeyboardEvent, currentIndex: number) => {
      let nextIndex: number | null = null;
      let step = 0;

      switch (event.key) {
        case 'ArrowRight':
          step = 1;
          nextIndex = currentIndex + 1;
          break;
        case 'ArrowLeft':
          step = -1;
          nextIndex = currentIndex - 1;
          break;
        case 'ArrowDown':
          step = 7;
          nextIndex = currentIndex + 7;
          break;
        case 'ArrowUp':
          step = -7;
          nextIndex = currentIndex - 7;
          break;
        default:
          return;
      }

      event.preventDefault();

      // Navigate to previous month
      if (nextIndex < 0) {
        if (onNavigateMonth) {
          onNavigateMonth('prev');
          // After month change, focus wraps to equivalent position from bottom
          setPendingFocusIndex(42 + nextIndex);
        }
        return;
      }

      // Navigate to next month
      if (nextIndex >= 42) {
        if (onNavigateMonth) {
          onNavigateMonth('next');
          setPendingFocusIndex(nextIndex - 42);
        }
        return;
      }

      // Skip disabled days while preserving navigation direction (±7 for vertical)
      const enabledIndex = findNextEnabledIndex(nextIndex, step);
      if (enabledIndex !== null) {
        buttonRefs.current[enabledIndex]?.focus();
      }
    },
    [findNextEnabledIndex, onNavigateMonth],
  );

  return (
    <>
      <Grid gridTemplateColumns="repeat(7, minmax(0, 1fr))" aria-hidden="true">
        {weekDays.map((day) => (
          <MText
            w="40px"
            key={`${month}-${day}`}
            textAlign="center"
            color="tGray.darkPurple"
            fontSize="12px"
          >
            {day.substring(0, 2)}
          </MText>
        ))}
      </Grid>
      <Grid
        gridTemplateColumns="repeat(7, minmax(0, 1fr))"
        aria-label={`${month} ${year}`}
      >
        {days.map((calendarDay, index) => (
          <DayButton
            key={calendarDay.$date.toString()}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            calendarDay={calendarDay}
            month={month}
            year={year}
            {...dayBtnPropsFn(calendarDay)}
            tabIndex={index === rovingIndex ? 0 : -1}
            onClick={() => onChange(calendarDay)}
            onEnter={onEnter}
            onGridKeyDown={(e) => handleGridKeyDown(e, index)}
          />
        ))}
      </Grid>
    </>
  );
};

interface DayButtonProps extends ButtonProps {
  calendarDay: DPDay;
  month: string;
  year: string;
  onEnter?: () => void;
  onGridKeyDown?: (event: React.KeyboardEvent) => void;
}

const DayButton = forwardRef<HTMLButtonElement, DayButtonProps>(
  ({ calendarDay, month, year, onEnter, onGridKeyDown, ...rest }, ref) => {
    const {
      day: dayWithLeadingZero,
      disabled,
      inCurrentMonth,
      now,
      selected,
      $date,
    } = calendarDay;
    const day = dayWithLeadingZero.replace(REMOVE_LEADING_ZERO_REGEX, '');
    let buttonProps: ButtonProps = {};
    // Text color goes on the inner span, not the button: a `color` on the
    // button only sets its resting color, and the `tertiary` variant's own
    // `_hover` color still takes over on hover. The span only inherits the
    // button's color, so the span's own color holds in every state.
    let buttonTextProps: BoxProps = { color: 'fg.primary' };
    // Store in state and lazy evaluate to avoid re-rendering and formatting the date on every render
    const [baseAriaLabel] = useState(() =>
      inCurrentMonth
        ? `${month} ${day}, ${year}`
        : formatDate($date, 'MMMM d, yyyy'),
    );
    let ariaLabel = baseAriaLabel;

    if (disabled) {
      buttonTextProps = {
        opacity: 0.7,
        color: selected ? 'fg.onAccent' : 'fg.muted',
      };
    } else if (!inCurrentMonth) {
      buttonProps = {
        ...buttonProps,
        _hover: {
          fontWeight: 700,
          bg: 'tBlue.hover',
        },
      };
      buttonTextProps = {
        color: 'fg.disabled',
        _groupHover: { color: 'fg.primary' },
      };
    }

    if (selected) {
      ariaLabel = `${ariaLabel} selected`;
      buttonTextProps = {
        ...buttonTextProps,
        color: 'fg.onAccent',
        _groupHover: undefined,
      };
      buttonProps = {
        ...buttonProps,
        bg: 'tIndigo.base',
        fontWeight: 700,
        _hover: {
          bg: 'tIndigo.base',
        },
        _focus: {
          bg: 'tIndigo.base',
        },
        _focusVisible: {
          boxShadow: 'inset 0 0 0 2px white',
        },
      };
    }

    if (now) {
      ariaLabel = `Today, ${ariaLabel}`;
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && onEnter) {
        event.preventDefault();
        onEnter();
        return;
      }
      if (
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
      ) {
        onGridKeyDown?.(event);
      }
    };

    return (
      <MButton
        ref={ref}
        aria-label={ariaLabel}
        variant="tertiary"
        minW="unset"
        w="40px"
        h="36px"
        className="group"
        isDisabled={disabled}
        fontWeight={400}
        borderRadius="3"
        position="relative"
        _hover={
          disabled
            ? undefined
            : {
                fontWeight: 700,
                bg: 'tBlue.hover',
              }
        }
        _focus={{
          bg: 'inherit',
        }}
        _focusVisible={{
          boxShadow: 'focus',
        }}
        onKeyDown={handleKeyDown}
        {...buttonProps}
        {...rest}
      >
        <MBox as={'span'} {...buttonTextProps}>
          {day}
        </MBox>
        {now && (
          <MBox
            as="span"
            position="absolute"
            bottom="3px"
            w="4px"
            h="4px"
            borderRadius="full"
            bg={selected ? 'white' : 'tIndigo.base'}
          />
        )}
      </MButton>
    );
  },
);
